/**
 * Tool-call event reconciliation shared across agent chat surfaces.
 *
 * Parallel tool execution can expose a React state race: the websocket may
 * deliver a tool_result while the matching tool_call state update is still
 * pending. These helpers keep matching strict by tool_call_id when available
 * and buffer unmatched results until the corresponding card is present.
 */

function isObject(value) {
  return value !== null && typeof value === 'object';
}

function isToolCallMessage(message) {
  return !!(
    isObject(message) &&
    (
      message.role === 'tool_call' ||
      message.role === 'TOOL_CALL' ||
      message.type === 'tool_call'
    )
  );
}

function getToolCallId(message) {
  const id = message?.toolCallId ?? message?.tool_call_id;
  return id == null ? null : String(id);
}

function getToolName(message) {
  return message?.toolName ?? message?.tool_name ?? null;
}

function getExecutionId(message) {
  const id = message?.executionId ?? message?.execution_id ?? message?.execution;
  return id == null ? null : String(id);
}

function hasToolResult(message) {
  if (!isObject(message)) return false;
  if ('toolResult' in message) {
    return message.toolResult !== undefined && message.toolResult !== null;
  }
  if ('result' in message) {
    return message.result !== undefined && message.result !== null;
  }
  return false;
}

function getResultField(message) {
  return (
    Object.prototype.hasOwnProperty.call(message, 'toolResult') ||
    message?.role === 'TOOL_CALL'
  )
    ? 'toolResult'
    : 'result';
}

function getSuccessField(message) {
  return (
    Object.prototype.hasOwnProperty.call(message, 'toolSuccess') ||
    Object.prototype.hasOwnProperty.call(message, 'toolResult') ||
    message?.role === 'TOOL_CALL'
  )
    ? 'toolSuccess'
    : 'success';
}

function getToolResultSuccess(event, defaultSuccess) {
  if (event.success !== undefined) return event.success;
  if (isObject(event.result) && Object.prototype.hasOwnProperty.call(event.result, 'success')) {
    return event.result.success;
  }
  return defaultSuccess;
}

function normalizeToolResultEvent(event) {
  if (!event) return null;
  const toolCallId = event.toolCallId ?? event.tool_call_id ?? null;
  const toolName = event.toolName ?? event.tool_name ?? null;
  return {
    toolCallId: toolCallId == null ? null : String(toolCallId),
    toolName,
    result: event.result,
    success: event.success,
  };
}

function findToolResultTargetIndex(messages, event) {
  const exactId = event.toolCallId;
  const toolName = event.toolName;

  if (exactId) {
    for (let i = messages.length - 1; i >= 0; i--) {
      const message = messages[i];
      if (isToolCallMessage(message) && getToolCallId(message) === exactId && !hasToolResult(message)) {
        return i;
      }
    }
    for (let i = messages.length - 1; i >= 0; i--) {
      const message = messages[i];
      if (isToolCallMessage(message) && getToolCallId(message) === exactId) {
        return i;
      }
    }
    return -1;
  }

  if (toolName) {
    for (let i = messages.length - 1; i >= 0; i--) {
      const message = messages[i];
      if (isToolCallMessage(message) && getToolName(message) === toolName && !hasToolResult(message)) {
        return i;
      }
    }
  }

  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    if (isToolCallMessage(message) && !hasToolResult(message)) {
      return i;
    }
  }
  return -1;
}

/**
 * Apply a tool_result event to the matching top-level tool_call message.
 * Returns the original messages reference when no match is available.
 *
 * @param {Array<any>} messages
 * @param {{toolCallId?: string|null, tool_call_id?: string|null, toolName?: string, tool_name?: string, result: any, success?: boolean}} event
 * @param {{defaultSuccess?: boolean}} options
 * @returns {{messages: Array<any>, applied: boolean, index: number}}
 */
export function applyToolResultToMessages(messages, event, options = {}) {
  if (!Array.isArray(messages)) {
    return { messages, applied: false, index: -1 };
  }

  const normalized = normalizeToolResultEvent(event);
  if (!normalized) {
    return { messages, applied: false, index: -1 };
  }

  const index = findToolResultTargetIndex(messages, normalized);
  if (index < 0) {
    return { messages, applied: false, index: -1 };
  }

  const message = messages[index];
  const resultField = getResultField(message);
  const successField = getSuccessField(message);
  const updated = [...messages];
  updated[index] = {
    ...message,
    [resultField]: normalized.result,
    [successField]: getToolResultSuccess(normalized, options.defaultSuccess ?? true),
  };

  return { messages: updated, applied: true, index };
}

function pendingToolResultKey(event) {
  const normalized = normalizeToolResultEvent(event);
  if (!normalized) return null;
  if (normalized.toolCallId) return `id:${normalized.toolCallId}`;
  if (normalized.toolName) return `name:${normalized.toolName}`;
  return 'unknown';
}

/**
 * Store an unmatched tool_result for a future flush.
 *
 * ID-keyed results replace the previous entry because a tool_call_id is unique
 * per execution step. Name-keyed results remain queued because older payloads
 * without ids may legitimately contain multiple same-name calls.
 *
 * @param {Record<string, Array<any>>} pending
 * @param {any} event
 * @returns {boolean}
 */
export function bufferPendingToolResult(pending, event) {
  if (!pending || typeof pending !== 'object') return false;
  const normalized = normalizeToolResultEvent(event);
  const key = pendingToolResultKey(normalized);
  if (!normalized || !key) return false;

  if (normalized.toolCallId) {
    pending[key] = [normalized];
  } else {
    if (!pending[key]) pending[key] = [];
    pending[key].push(normalized);
  }
  return true;
}

/**
 * Drain buffered top-level tool results into messages. Unmatched events remain
 * buffered so later tool_call cards can still receive them.
 *
 * @param {Array<any>} messages
 * @param {Record<string, Array<any>>} pending
 * @param {{defaultSuccess?: boolean}} options
 * @returns {Array<any>}
 */
export function flushPendingToolResults(messages, pending, options = {}) {
  if (!Array.isArray(messages) || !pending || typeof pending !== 'object') {
    return messages;
  }

  let current = messages;
  for (const key of Object.keys(pending)) {
    const queue = pending[key] || [];
    const remaining = [];

    for (const event of queue) {
      const applied = applyToolResultToMessages(current, event, options);
      if (applied.applied) {
        current = applied.messages;
      } else {
        remaining.push(event);
      }
    }

    if (remaining.length > 0) {
      pending[key] = remaining;
    } else {
      delete pending[key];
    }
  }

  return current;
}

function normalizePersistedToolMessage(message) {
  if (!isToolCallMessage(message)) return null;
  const result = message.tool_output ?? message.toolResult ?? message.result;
  const subAgentEvents = Array.isArray(message.sub_agent_events)
    ? message.sub_agent_events
    : Array.isArray(message.subAgentEvents)
      ? message.subAgentEvents
      : null;
  if ((result === undefined || result === null) && !subAgentEvents) return null;

  const toolCallId = message.tool_call_id ?? message.toolCallId ?? message.id ?? null;
  return {
    toolCallId: toolCallId == null ? null : String(toolCallId),
    toolName: message.tool_name ?? message.toolName ?? null,
    toolRationale: message.tool_rationale ?? message.toolRationale ?? null,
    toolGroupRationale: message.tool_group_rationale ?? message.toolGroupRationale ?? null,
    result,
    success: message.success ?? message.toolSuccess,
    subAgentEvents,
  };
}

function updateToolMessageAt(messages, index, event, options = {}) {
  const message = messages[index];
  const resultField = getResultField(message);
  const successField = getSuccessField(message);
  const nextMessage = { ...message };
  if (event.result !== undefined && event.result !== null) {
    nextMessage[resultField] = event.result;
    nextMessage[successField] = getToolResultSuccess(
      event,
      options.defaultSuccess ?? true,
    );
  }
  if (Array.isArray(event.subAgentEvents) && event.subAgentEvents.length > 0) {
    nextMessage.subAgentEvents = event.subAgentEvents;
  }
  if (event.toolRationale && !nextMessage.toolRationale) {
    nextMessage.toolRationale = event.toolRationale;
  }
  if (event.toolGroupRationale && !nextMessage.toolGroupRationale) {
    nextMessage.toolGroupRationale = event.toolGroupRationale;
  }
  const updated = [...messages];
  updated[index] = nextMessage;
  return updated;
}

/**
 * Merge persisted tool outputs from API-loaded conversation/execution messages
 * into live UI cards. This is the durable fallback for missed websocket
 * tool_result events while an execution is still running.
 *
 * Matching order:
 *   1. Exact tool_call_id.
 *   2. Persisted/live call order for unresolved same-name cards.
 *
 * @param {Array<any>} messages
 * @param {Array<any>} persistedMessages
 * @param {{defaultSuccess?: boolean}} options
 * @returns {Array<any>}
 */
export function mergePersistedToolResults(messages, persistedMessages, options = {}) {
  if (!Array.isArray(messages) || !Array.isArray(persistedMessages)) {
    return messages;
  }

  const persistedResults = persistedMessages
    .map(normalizePersistedToolMessage)
    .filter(Boolean);
  if (persistedResults.length === 0) {
    return messages;
  }

  let current = messages;
  const usedIndexes = new Set();

  for (const event of persistedResults) {
    let targetIndex = -1;

    if (event.toolCallId) {
      for (let i = current.length - 1; i >= 0; i--) {
        if (
          !usedIndexes.has(i) &&
          isToolCallMessage(current[i]) &&
          getToolCallId(current[i]) === event.toolCallId
        ) {
          targetIndex = i;
          break;
        }
      }
    }

    if (targetIndex < 0 && event.toolName) {
      for (let i = 0; i < current.length; i++) {
        if (
          !usedIndexes.has(i) &&
          isToolCallMessage(current[i]) &&
          getToolName(current[i]) === event.toolName &&
          !hasToolResult(current[i])
        ) {
          targetIndex = i;
          break;
        }
      }
    }

    if (targetIndex >= 0) {
      current = updateToolMessageAt(current, targetIndex, event, options);
      usedIndexes.add(targetIndex);
    }
  }

  return current;
}

function mergeLiveToolEvidence(persistedMessage, liveMessage) {
  const merged = { ...persistedMessage };

  if (!hasToolResult(persistedMessage) && hasToolResult(liveMessage)) {
    const liveResult = Object.prototype.hasOwnProperty.call(liveMessage, 'toolResult')
      ? liveMessage.toolResult
      : liveMessage.result;
    merged[getResultField(persistedMessage)] = liveResult;
    merged[getSuccessField(persistedMessage)] = (
      liveMessage.toolSuccess ?? liveMessage.success
    );
  }

  if (
    (!Array.isArray(merged.subAgentEvents) || merged.subAgentEvents.length === 0) &&
    Array.isArray(liveMessage.subAgentEvents) &&
    liveMessage.subAgentEvents.length > 0
  ) {
    merged.subAgentEvents = liveMessage.subAgentEvents;
  }
  if (!merged.toolRationale && liveMessage.toolRationale) {
    merged.toolRationale = liveMessage.toolRationale;
  }
  if (!merged.toolGroupRationale && liveMessage.toolGroupRationale) {
    merged.toolGroupRationale = liveMessage.toolGroupRationale;
  }

  return merged;
}

function messageTimestamp(message) {
  const value = message?.timestamp ?? message?.created_at;
  const timestamp = value instanceof Date ? value.getTime() : Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : null;
}

/**
 * Reconcile the immediate post-completion session read with the live stream.
 *
 * The session endpoint is authoritative for rows it has already persisted, but
 * a completion event and the following REST read can race across workers. In
 * that window, replacing state outright removes tool cards that the websocket
 * already delivered. Preserve only the completed execution's current-turn tool
 * cards (and final assistant message) when the snapshot has not caught up yet.
 *
 * @param {Array<any>} liveMessages
 * @param {Array<any>} persistedMessages
 * @param {string|number} executionId
 * @returns {Array<any>}
 */
export function reconcileCompletionSessionMessages(
  liveMessages,
  persistedMessages,
  executionId,
) {
  if (!Array.isArray(liveMessages) || !Array.isArray(persistedMessages)) {
    return Array.isArray(persistedMessages) ? persistedMessages : liveMessages;
  }

  const targetExecutionId = executionId == null ? null : String(executionId);
  if (!targetExecutionId) return persistedMessages;

  let liveAssistantIndex = -1;
  for (let i = liveMessages.length - 1; i >= 0; i--) {
    if (
      liveMessages[i]?.role === 'ASSISTANT' &&
      getExecutionId(liveMessages[i]) === targetExecutionId
    ) {
      liveAssistantIndex = i;
      break;
    }
  }

  let turnStartIndex = -1;
  if (liveAssistantIndex >= 0) {
    for (let i = liveAssistantIndex - 1; i >= 0; i--) {
      if (String(liveMessages[i]?.role || '').toUpperCase() === 'USER') {
        turnStartIndex = i;
        break;
      }
    }
  }

  const liveTurnMessages = liveAssistantIndex >= 0
    ? liveMessages.slice(turnStartIndex + 1, liveAssistantIndex + 1)
    : liveMessages.filter(
      (message) => getExecutionId(message) === targetExecutionId,
    );
  const liveToolMessages = liveTurnMessages.filter((message) => {
    if (!isToolCallMessage(message)) return false;
    const messageExecutionId = getExecutionId(message);
    return !messageExecutionId || messageExecutionId === targetExecutionId;
  });
  const liveAssistant = liveTurnMessages.find(
    (message) => (
      message?.role === 'ASSISTANT' &&
      getExecutionId(message) === targetExecutionId
    ),
  );

  const next = persistedMessages.map((message) => ({ ...message }));
  const usedPersistedIndexes = new Set();
  const missingLiveMessages = [];

  for (const liveTool of liveToolMessages) {
    const liveToolCallId = getToolCallId(liveTool);
    const liveToolName = getToolName(liveTool);
    let persistedIndex = -1;

    if (liveToolCallId) {
      persistedIndex = next.findIndex(
        (message, index) => (
          !usedPersistedIndexes.has(index) &&
          isToolCallMessage(message) &&
          getToolCallId(message) === liveToolCallId
        ),
      );
    }

    if (persistedIndex < 0 && liveToolName) {
      persistedIndex = next.findIndex(
        (message, index) => {
          const messageExecutionId = getExecutionId(message);
          return (
            !usedPersistedIndexes.has(index) &&
            isToolCallMessage(message) &&
            getToolName(message) === liveToolName &&
            (!messageExecutionId || messageExecutionId === targetExecutionId)
          );
        },
      );
    }

    if (persistedIndex >= 0) {
      next[persistedIndex] = mergeLiveToolEvidence(
        next[persistedIndex],
        liveTool,
      );
      usedPersistedIndexes.add(persistedIndex);
    } else {
      missingLiveMessages.push(liveTool);
    }
  }

  const hasPersistedAssistant = next.some(
    (message) => (
      message?.role === 'ASSISTANT' &&
      getExecutionId(message) === targetExecutionId
    ),
  );
  if (liveAssistant && !hasPersistedAssistant) {
    missingLiveMessages.push(liveAssistant);
  }
  if (missingLiveMessages.length === 0) return next;

  return [...next, ...missingLiveMessages]
    .map((message, index) => ({ message, index, timestamp: messageTimestamp(message) }))
    .sort((left, right) => {
      if (left.timestamp === null || right.timestamp === null) {
        return left.index - right.index;
      }
      return left.timestamp - right.timestamp || left.index - right.index;
    })
    .map(({ message }) => message);
}

export function hasPendingToolCallMessages(messages) {
  return Array.isArray(messages) && messages.some(
    (message) => isToolCallMessage(message) && !hasToolResult(message)
  );
}

export function clearPendingToolResults(pending) {
  if (!pending || typeof pending !== 'object') return;
  for (const key of Object.keys(pending)) {
    delete pending[key];
  }
}
